/**
 * Etapa 3 — Plano de parcelas e prazos.
 *
 * Permite criar multiplos sub-planos de parcelamento (Diario / Semanal /
 * Quinzenal / Mensal / Personalizado), cada um com sua taxa e numero de
 * parcelas. Sao salvos no state.installments e persistidos com o plano.
 */

import { useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Field, NumberInput, PercentInput, Select } from "../components/Field";
import { InfoCallout, SectionCard } from "../components/SectionCard";
import { Checkbox, Toggle } from "../components/Toggle";
import { WeekdayPicker } from "../components/WeekdayPicker";
import type { PlanFormState } from "../types";
import type {
  ChargePlanInstallment,
  ChargePlanInstallmentTipo,
} from "../../../../services/charge-plans.service";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

const TIPO_OPTIONS: {
  value: ChargePlanInstallmentTipo;
  label: string;
  desc: string;
}[] = [
  { value: "diario", label: "Diario", desc: "Cobranca e parcelas diarias" },
  { value: "semanal", label: "Semanal", desc: "Cobranca e parcelas semanais" },
  { value: "quinzenal", label: "Quinzenal", desc: "Cobranca e parcelas quinzenais" },
  { value: "mensal", label: "Mensal", desc: "Cobranca e parcelas mensais" },
  { value: "personalizado", label: "Personalizado", desc: "Crie um plano do seu jeito" },
];

const TIPO_LABEL: Record<ChargePlanInstallmentTipo, string> = {
  diario: "Diario",
  semanal: "Semanal",
  quinzenal: "Quinzenal",
  mensal: "Mensal",
  personalizado: "Personalizado",
};

// Sufixo de periodo usado no label "% ao dia / por semana / ...".
const TAXA_PERIODO: Record<ChargePlanInstallmentTipo, string> = {
  diario: "ao dia",
  semanal: "por semana",
  quinzenal: "por quinzena",
  mensal: "ao mes",
  personalizado: "por periodo",
};

// "Periodo" (singular) usado no callout de exemplo: "X dias / X semanas / ..."
const PERIODO_SINGULAR: Record<ChargePlanInstallmentTipo, string> = {
  diario: "dia",
  semanal: "semana",
  quinzenal: "quinzena",
  mensal: "mes",
  personalizado: "periodo",
};

const PERIODO_PLURAL: Record<ChargePlanInstallmentTipo, string> = {
  diario: "dias",
  semanal: "semanas",
  quinzenal: "quinzenas",
  mensal: "meses",
  personalizado: "periodos",
};

export function Step3Installments({ state, setState }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftInstallment>(emptyDraft());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const set = <K extends keyof PlanFormState>(key: K, value: PlanFormState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    setError(null);
    const minN = parseInt(draft.numParcelasMin, 10);
    const maxN = parseInt(draft.numParcelasMax, 10);
    if (!Number.isFinite(minN) || minN < 1) {
      setError("Quantidade minima de parcelas deve ser >= 1");
      return;
    }
    if (!Number.isFinite(maxN) || maxN < 1) {
      setError("Quantidade maxima de parcelas deve ser >= 1");
      return;
    }
    if (maxN < minN) {
      setError("Quantidade maxima deve ser maior ou igual a minima");
      return;
    }
    if (draft.tipo === "personalizado" && (draft.intervaloDias ?? 0) < 1) {
      setError("Informe o intervalo em dias para o plano personalizado");
      return;
    }

    // Vazio ou invalido vira "0" no salvar (taxa zero = sem juros).
    const taxaNum = parseFloat(draft.taxaPct);
    const taxaFinal = Number.isFinite(taxaNum) && taxaNum >= 0 ? taxaNum : 0;

    const newItem: ChargePlanInstallment = {
      tipo: draft.tipo,
      label: draft.label || TIPO_LABEL[draft.tipo],
      taxaPct: String(taxaFinal),
      numParcelas: maxN,
      numParcelasMin: minN,
      intervaloDias:
        draft.tipo === "personalizado" ? draft.intervaloDias : null,
      ativo: draft.ativo,
      ordem: editingIndex ?? state.installments.length,
    };

    if (editingIndex !== null) {
      const next = [...state.installments];
      next[editingIndex] = { ...next[editingIndex], ...newItem };
      setState({ ...state, installments: next });
    } else {
      setState({ ...state, installments: [...state.installments, newItem] });
    }

    setDraft(emptyDraft());
    setEditingIndex(null);
  };

  const handleEdit = (idx: number) => {
    const i = state.installments[idx];
    setDraft({
      tipo: i.tipo,
      label: i.label ?? "",
      taxaPct: i.taxaPct ?? "",
      // Compat: planos antigos so tem numParcelas (max); usa o mesmo como min.
      numParcelasMin: String(i.numParcelasMin ?? i.numParcelas),
      numParcelasMax: String(i.numParcelas),
      intervaloDias: i.intervaloDias ?? undefined,
      ativo: i.ativo ?? true,
    });
    setEditingIndex(idx);
  };

  const handleRemove = (idx: number) => {
    setState({
      ...state,
      installments: state.installments.filter((_, i) => i !== idx),
    });
    if (editingIndex === idx) {
      setDraft(emptyDraft());
      setEditingIndex(null);
    }
  };

  const handleCancelEdit = () => {
    setDraft(emptyDraft());
    setEditingIndex(null);
    setError(null);
  };

  const toggleActive = (idx: number) => {
    const next = [...state.installments];
    next[idx] = { ...next[idx], ativo: !next[idx].ativo };
    setState({ ...state, installments: next });
  };

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          2. Plano de parcelas e prazos
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure como serao as parcelas e os prazos disponiveis para o seu cliente.
        </p>
      </header>

      {/* Bloco "Dias da semana para cobrar" — movido da etapa "Regras da
          cobranca" porque faz mais sentido perto da definicao das parcelas. */}
      <SectionCard
        icon={<CalendarIcon />}
        title={t("charge_plans.step2.weekdays.title")}
        description={t("charge_plans.step2.weekdays.description")}
        required
      >
        <WeekdayPicker
          value={state.diasSemana}
          onChange={(v) => set("diasSemana", v)}
        />
        <div className="mt-4">
          <Checkbox
            checked={state.incluirFeriados}
            onChange={(v) => set("incluirFeriados", v)}
            label={t("charge_plans.step2.weekdays.include_holidays")}
          />
        </div>
        <div className="mt-4">
          <InfoCallout>
            {t("charge_plans.step2.weekdays.callout")}
          </InfoCallout>
        </div>
      </SectionCard>

      <InfoCallout>
        Voce pode usar os planos padroes ou criar planos personalizados do seu jeito.
      </InfoCallout>

      {/* Form de adicao/edicao */}
      <SectionCard
        icon={<PlusIcon />}
        title={editingIndex !== null ? "Editar plano" : "Criar novo plano"}
        description="Escolha um tipo de plano ou personalize do seu jeito."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label="Nome do plano">
            <Select
              value={draft.tipo}
              onChange={(v) => setDraft({ ...draft, tipo: v as ChargePlanInstallmentTipo })}
              options={TIPO_OPTIONS.map((o) => ({
                value: o.value,
                label: `${o.label} — ${o.desc}`,
              }))}
            />
          </Field>
          <Field label={`Taxa (% ${TAXA_PERIODO[draft.tipo]})`}>
            <PercentInput
              value={draft.taxaPct}
              onChange={(v) => setDraft({ ...draft, taxaPct: v })}
              placeholder="Ex.: 1,35"
            />
          </Field>
          <Field label="Mínimo de parcelas">
            <div className="relative">
              <input
                type="number"
                min={1}
                value={draft.numParcelasMin}
                onChange={(e) =>
                  setDraft({ ...draft, numParcelasMin: e.target.value })
                }
                placeholder="Ex.: 10"
                className="h-11 w-full rounded-lg border border-input bg-card px-3 pr-16 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                parcelas
              </span>
            </div>
          </Field>
          <Field label="Máximo de parcelas">
            <div className="relative">
              <input
                type="number"
                min={1}
                value={draft.numParcelasMax}
                onChange={(e) =>
                  setDraft({ ...draft, numParcelasMax: e.target.value })
                }
                placeholder="Ex.: 20"
                className="h-11 w-full rounded-lg border border-input bg-card px-3 pr-16 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                parcelas
              </span>
            </div>
          </Field>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label="Status">
            <Select
              value={draft.ativo ? "ativo" : "inativo"}
              onChange={(v) => setDraft({ ...draft, ativo: v === "ativo" })}
              options={[
                { value: "ativo", label: "Ativo" },
                { value: "inativo", label: "Inativo" },
              ]}
            />
          </Field>
        </div>

        {draft.tipo === "personalizado" ? (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="Intervalo entre parcelas (dias)"
              hint="Ex.: 10 dias entre cada parcela"
            >
              <NumberInput
                value={String(draft.intervaloDias ?? "")}
                onChange={(v) =>
                  setDraft({ ...draft, intervaloDias: parseInt(v, 10) || 0 })
                }
                suffix="dias"
              />
            </Field>
            <Field label="Rotulo (opcional)" hint="Ex.: Quinzenal especial">
              <input
                type="text"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </Field>
          </div>
        ) : null}

        {error ? (
          <p className="mt-3 text-sm text-error">{error}</p>
        ) : (
          <div className="mt-4">
            <InfoCallout variant="tip">
              <ExampleCalc draft={draft} />
            </InfoCallout>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          {editingIndex !== null ? (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
            >
              Cancelar
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleAdd}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            {editingIndex !== null ? "Salvar alteracoes" : "Adicionar plano"}
          </button>
        </div>
      </SectionCard>

      {/* Lista dos sub-planos adicionados */}
      <SectionCard
        icon={<ListIcon />}
        title="Planos disponiveis para o cliente"
        description="Esses sao os planos de parcelamento que o cliente vera no app."
      >
        {state.installments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            Nenhum plano adicionado ainda. Use o formulario acima para criar.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {state.installments.map((item, idx) => (
              <InstallmentRow
                key={`${item.tipo}-${idx}`}
                item={item}
                onEdit={() => handleEdit(idx)}
                onRemove={() => handleRemove(idx)}
                onToggle={() => toggleActive(idx)}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ====== Sub-componentes ======

/**
 * Callout com exemplo de calculo usando R$ 1.000 como base.
 * Aplica juros SIMPLES: total = base * (1 + taxa * periodos).
 * Exibe o resultado pro MAX da faixa (pior caso pro cliente).
 */
function ExampleCalc({ draft }: { draft: DraftInstallment }) {
  const taxa = parseFloat(draft.taxaPct);
  const min = parseInt(draft.numParcelasMin, 10);
  const max = parseInt(draft.numParcelasMax, 10);

  const taxaValida = Number.isFinite(taxa) && taxa >= 0;
  const minValido = Number.isFinite(min) && min >= 1;
  const maxValido = Number.isFinite(max) && max >= min;

  if (!taxaValida || !minValido || !maxValido) {
    return (
      <p>
        Defina a taxa e a faixa de parcelas para ver um exemplo de calculo
        baseado em R$ 1.000.
      </p>
    );
  }

  const base = 1000;
  const taxaDec = taxa / 100;
  const acrescimoTotal = base * taxaDec * max;
  const total = base + acrescimoTotal;
  const valorParcela = total / max;

  const fmt = (v: number) =>
    v.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  return (
    <div className="space-y-1">
      <p>
        <strong>Exemplo:</strong> com R$ 1.000,00 no plano <strong>{TIPO_LABEL[draft.tipo]}</strong>
        {" "}a <strong>{taxa.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}% {TAXA_PERIODO[draft.tipo]}</strong>,
        em <strong>{max} {max === 1 ? "parcela" : "parcelas"}</strong>
        {" "}({max} {max === 1 ? PERIODO_SINGULAR[draft.tipo] : PERIODO_PLURAL[draft.tipo]}):
      </p>
      <ul className="ml-4 list-disc space-y-0.5">
        <li>
          Acréscimo total: <strong>{fmt(acrescimoTotal)}</strong>
          {" "}({(taxa * max).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}%)
        </li>
        <li>
          Valor total: <strong>{fmt(total)}</strong>
        </li>
        <li>
          Parcela: <strong>{fmt(valorParcela)}</strong> × {max}
        </li>
      </ul>
      <p className="text-xs text-muted-foreground">
        O cliente vai poder escolher entre <strong>{min}</strong> e{" "}
        <strong>{max}</strong> parcelas ao resgatar o codigo.
      </p>
    </div>
  );
}

function InstallmentRow({
  item,
  onEdit,
  onRemove,
  onToggle,
}: {
  item: ChargePlanInstallment;
  onEdit: () => void;
  onRemove: () => void;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <CalendarIcon />
        </div>
        <div>
          <h4 className="text-sm font-bold text-foreground">
            {item.label || TIPO_LABEL[item.tipo]}
          </h4>
          <p className="text-xs text-muted-foreground">
            {item.numParcelasMin && item.numParcelasMin !== item.numParcelas
              ? `${item.numParcelasMin}–${item.numParcelas} parcelas`
              : `${item.numParcelas} parcelas`}
            {item.taxaPct ? ` · taxa ${item.taxaPct}% ${TAXA_PERIODO[item.tipo]}` : ""}
            {item.intervaloDias ? ` · a cada ${item.intervaloDias} dias` : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Toggle checked={item.ativo ?? true} onChange={onToggle} />
          <span className="text-xs text-muted-foreground">
            {item.ativo === false ? "Inativo" : "Ativo"}
          </span>
        </div>
        <button
          onClick={onEdit}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Editar"
        >
          <EditIcon />
        </button>
        <button
          onClick={onRemove}
          className="rounded-lg p-2 text-muted-foreground hover:bg-error/10 hover:text-error"
          title="Remover"
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

// ====== Helpers ======

interface DraftInstallment {
  tipo: ChargePlanInstallmentTipo;
  label: string;
  // String pra permitir campo vazio enquanto o gestor edita (NumberInput
  // controlado nao consegue limpar "0" quando a state e number).
  taxaPct: string;
  // Faixa min/max — cliente escolhe a quantidade dentro dessa faixa ao
  // resgatar o codigo. Strings pra permitir edicao livre.
  numParcelasMin: string;
  numParcelasMax: string;
  intervaloDias?: number;
  ativo: boolean;
}

function emptyDraft(): DraftInstallment {
  return {
    tipo: "diario",
    label: "",
    taxaPct: "",
    numParcelasMin: "10",
    numParcelasMax: "20",
    intervaloDias: undefined,
    ativo: true,
  };
}

// ====== Icones ======

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}
